import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LoginServices } from '../../../services/login.service';
import BgBody from '../../../components/layout/bg-body.component';
import Input from '../../../components/forms/input.component';
import Button from '../../../components/forms/button.component';
import BgPages from '../../../hooks/use-bg-pages.hook';
import Icon from '../../../components/forms/icon.component';

declare const KTComponents: any;

/**
 * `POST /v1/auth/forgot-password` es idempotente a propósito: siempre responde
 * 200 aunque el identifier no exista, para no permitir enumerar usuarios. Por
 * eso la pantalla muestra el mismo mensaje en todos los casos.
 */
export default function ForgotPasswordPage() {
  BgPages();
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues: { identifier: '' }
  });

  useEffect(() => {
    KTComponents.init();
  }, []);

  const submit = async (data: any) => {
    setErrorMessage('');
    try {
      await LoginServices.forgotPassword({ identifier: data.identifier });
      setSent(true);
    } catch (error: any) {
      console.error(error);
      setErrorMessage(String(error?.error || error?.status || 'No se pudo enviar la solicitud'));
    }
  };

  return (
    <>
      <BgBody />
      <div className="login-hero login-hero--auth text-white bg-p-dark">
        <div className="login-shell--auth d-flex justify-content-center px-2 px-sm-4 position-relative w-100">
          <div className="login-flat login-card--auth device-card device-card--glow shadow-xxl d-flex flex-column justify-content-between">
            <div className="text-center mb-md-10 mt-10">
              <img
                alt="Logo"
                src="/assets/media/logos/voices-gnl-dark.png"
                className="h-45px h-lg-155px logo-default mb-3 mb-md-5 logob z-index-4 img-fluid"
              />
              <h1 className="fs-2x fw-bold mb-1 text-white">Recuperar contraseña</h1>
              <div className="text-gray-500 fs-6 fs-md-4">
                Te mandamos un enlace al correo de tu cuenta.
              </div>
            </div>

            {errorMessage && (
              <div className="alert alert-danger d-flex align-items-center p-2 bg-transparent animated-Alert mt-4 mb-5">
                <Icon icon="ki-shield-cross" size="fs-2hx" type="danger" className="me-4" />
                <div className="d-flex flex-column flex-grow-1">
                  <h4 className="mb-1 text-white">{t('errors.title-text-alert-atencion')}</h4>
                  <span>{errorMessage}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-icon bg-transparent ms-auto"
                  onClick={() => setErrorMessage('')}
                >
                  <Icon icon="ki-cross" size="fs-1" type="white" />
                </button>
              </div>
            )}

            {sent ? (
              <div className="mb-10">
                <div className="alert d-flex align-items-center p-4 bg-transparent border border-success rounded">
                  <Icon icon="ki-shield-tick" size="fs-2hx" type="success" className="me-4" />
                  <div className="d-flex flex-column">
                    <h4 className="mb-1 text-white">Solicitud registrada</h4>
                    <span className="text-gray-500">
                      Si el usuario existe y tiene correo dado de alta, en unos minutos llega el
                      enlace para crear una contraseña nueva. El enlace es de un solo uso y expira.
                    </span>
                  </div>
                </div>
                <Link to="/auth/login" className="btn btn-lg btn-primary w-100 mt-6">
                  Volver a iniciar sesión
                </Link>
              </div>
            ) : (
              <form className="form mb-10" onSubmit={handleSubmit(submit)}>
                <div className="mb-8 inputb">
                  <Input
                    typeBg="text-white"
                    requiredIndicator="required"
                    uniqueTheme
                    label="Usuario o correo electrónico"
                    placeholder="Ingresa tu usuario o correo"
                    errors={errors.identifier}
                    {...register('identifier', {
                      required: 'Este campo es requerido',
                      maxLength: { value: 120, message: 'Máximo 120 caracteres' },
                      setValueAs: (v) => String(v ?? '').trim()
                    })}
                  />
                </div>

                <Button
                  type="submit"
                  className="btn btn-lg btn-primary w-100 mb-4"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                >
                  Enviar enlace
                </Button>

                <Link to="/auth/login" className="btn btn-lg btn-link text-gray-500 w-100">
                  Cancelar
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
